// IndustriesSection.jsx — Premium Redesign
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Building2,
  Briefcase,
  ShoppingBag,
  Compass,
  Megaphone,
  Truck,
  Droplet,
  Sprout,
  Factory,
  HeartPulse,
  Recycle,
  Handshake
} from "lucide-react";

const industries = [
  {
    name: "Government",
    icon: <Building2 size={28} />,
    desc: "We develop secure civic portals, municipal tax collection platforms, NMC law department case management systems, and automated grievance tracking applications that improve citizen engagement, reduce paperwork, and strengthen administrative workflow integrity across local government bodies.",
    tagline: "Civic Systems & Grievance Portals",
    techs: ["Database Security", "Civic CRM", "NM Portals"],
    color: "#60a5fa",
    gradient: "from-blue-500 to-indigo-600"
  },
  {
    name: "Consultant",
    icon: <Briefcase size={28} />,
    desc: "We build interactive client management databases, automated workflow schedulers, powerful B2B data analytics dashboards, and fully customized CRM systems that help business consultants streamline daily operations, track project milestones, and generate data-driven reports for strategic decision-making.",
    tagline: "B2B Analytics & Client CRM",
    techs: ["Workflow Automation", "Custom Reporting", "Analytics"],
    color: "#a78bfa",
    gradient: "from-violet-500 to-purple-600"
  },
  {
    name: "Ecommerce",
    icon: <ShoppingBag size={28} />,
    desc: "We engineer high-performance online stores featuring secure multi-gateway payment checkouts, automated real-time inventory synchronization, intelligent discount engines, customer loyalty programs, and robust user profiles — delivering seamless shopping experiences that convert visitors into repeat buyers.",
    tagline: "Retail Platforms & Gateways",
    techs: ["POS Integration", "Stock Sync", "High UX"],
    color: "#34d399",
    gradient: "from-emerald-400 to-teal-600"
  },
  {
    name: "Travel",
    icon: <Compass size={28} />,
    desc: "We design integrated travel booking engines, dynamic multi-day tour itinerary planners, real-time GPS navigation guides, cycling club membership portals, and event registration databases with secure online payment gateway integrations, making trip planning effortless for agencies and travelers alike.",
    tagline: "Booking Engines & Travel Planners",
    techs: ["GPS Mapping", "Event Schedules", "Invoicing"],
    color: "#fb923c",
    gradient: "from-orange-400 to-amber-600"
  },
  {
    name: "Media & Advertising",
    icon: <Megaphone size={28} />,
    desc: "We deploy intelligent transit advertising management systems, dynamic video streaming tools, programmatic campaign planning dashboards, and interactive audience survey portals that help media agencies measure reach, track impressions, optimize ad spend, and deliver real-time campaign performance analytics.",
    tagline: "Transit Ads & Programmatic Campaigns",
    techs: ["Video Streaming", "Outreach Analytics", "Campaigns"],
    color: "#f472b6",
    gradient: "from-pink-400 to-rose-600"
  },
  {
    name: "Logistics",
    icon: <Truck size={28} />,
    desc: "We construct end-to-end supply chain tracking databases, intelligent fleet management systems, live GPS vehicle integrations, AI-powered route optimizers, and smart warehouse inventory tools that minimize shipping delays, reduce operational costs, and maximize full supply chain transparency for logistics companies.",
    tagline: "Supply Chain & Route Optimizers",
    techs: ["Real-time GPS", "Inventory Control", "RFID"],
    color: "#38bdf8",
    gradient: "from-sky-400 to-cyan-600"
  },
  {
    name: "Energy",
    icon: <Droplet size={28} />,
    desc: "We build automated utility billing software, remote smart meter monitoring dashboards, real-time consumption trend charts, IoT-enabled sensor grids, and energy resource management platforms for municipal power and water departments that help reduce waste and enable data-driven infrastructure planning.",
    tagline: "Utility Billing & Remote Smart Meters",
    techs: ["IoT Dashboards", "Billing API", "Data Charts"],
    color: "#facc15",
    gradient: "from-yellow-400 to-amber-500"
  },
  {
    name: "Agriculture",
    icon: <Sprout size={28} />,
    desc: "We implement smart crop yield analytics databases, IoT-enabled farm sensor trackers, digital supply chain marketplace portals, automated supplier invoicing systems, and farmer advisory dashboards that help digitize agricultural ecosystems, improve harvest planning, and connect producers directly to buyers.",
    tagline: "Smart Farm Analytics & Marketplaces",
    techs: ["Sensor IoT", "Supply Trace", "Vendor Billing"],
    color: "#4ade80",
    gradient: "from-green-400 to-emerald-600"
  },
  {
    name: "Industries",
    icon: <Factory size={28} />,
    desc: "We design factory execution systems (MES), real-time inventory tracking databases, raw material logistics tools, digital quality checklist portals, and enterprise resource planning (ERP) platforms that connect the shop floor to management, boosting production efficiency and operational visibility.",
    tagline: "Manufacturing MES & Enterprise ERP",
    techs: ["Material Trace", "Quality Auditing", "POS"],
    color: "#94a3b8",
    gradient: "from-slate-400 to-slate-600"
  },
  {
    name: "Medical",
    icon: <HeartPulse size={28} />,
    desc: "We build HIPAA-compliant electronic health record (EHR) systems, virtual doctor appointment schedulers, telemedicine video consultation platforms, automated prescription management tools, and pharmacy inventory managers that elevate the quality of clinical care and reduce administrative overhead.",
    tagline: "EHR Portals & Telemedicine Video",
    techs: ["HIPAA Security", "Telehealth API", "Schedulers"],
    color: "#f87171",
    gradient: "from-red-400 to-rose-600"
  },
  {
    name: "Rubber",
    icon: <Recycle size={28} />,
    desc: "We engineer customized manufacturing process trackers, recycle batch management databases, automated QA checklist portals, B2B wholesale order dashboards, and end-to-end shipping pipeline tools designed specifically for rubber and polymer processing plants to ensure quality and operational traceability.",
    tagline: "Batch Trackers & QA Checklists",
    techs: ["Manufacturing Execution", "B2B Orders", "Pipelines"],
    color: "#a3e635",
    gradient: "from-lime-400 to-green-500"
  },
  {
    name: "NGO Trust",
    icon: <Handshake size={28} />,
    desc: "We construct secure donation tracking portals, verified trust member directories, transparent fund allocation dashboards, community fundraising campaign organizers, and beneficiary impact report tools that help NGOs and charitable trusts build donor trust, ensure accountability, and scale their social outreach.",
    tagline: "Donation Portals & Member Directories",
    techs: ["Payment Checkouts", "Fund Allocation", "Reports"],
    color: "#c084fc",
    gradient: "from-purple-400 to-fuchsia-600"
  }
];


const IndustriesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [hoveredNav, setHoveredNav] = useState(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });

  const handleNext = () => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % industries.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + industries.length) % industries.length);
  };

  useEffect(() => {
    const timer = setInterval(handleNext, 5500);
    return () => clearInterval(timer);
  }, [activeIndex]);

  const current = industries[activeIndex];
  const accentColor = current.color;

  const cardVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
      scale: 0.95,
      filter: "blur(8px)"
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        x: { type: "spring", stiffness: 280, damping: 26 },
        opacity: { duration: 0.4 },
        scale: { duration: 0.4 },
        filter: { duration: 0.3 }
      }
    },
    exit: (dir) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
      scale: 0.95,
      filter: "blur(8px)",
      transition: {
        x: { type: "spring", stiffness: 280, damping: 26 },
        opacity: { duration: 0.25 },
        filter: { duration: 0.2 }
      }
    })
  };

  const staggerParent = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <div ref={sectionRef} className="ind-root">

      {/* ── Animated Background ── */}
      <div className="ind-bg-layer">
        {/* Mesh gradient orbs */}
        <motion.div className="ind-orb ind-orb-1"
          animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{ background: `radial-gradient(circle, ${accentColor}22 0%, transparent 70%)` }}
        />
        <motion.div className="ind-orb ind-orb-2"
          animate={{ x: [0, -50, 0], y: [0, 50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div className="ind-orb ind-orb-3"
          animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0] }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
        />
        {/* Dot grid */}
        <div className="ind-dot-grid" />
        {/* Floating particles */}
        {[...Array(18)].map((_, i) => (
          <motion.div
            key={i}
            className="ind-particle"
            style={{
              left: `${5 + (i * 5.5) % 90}%`,
              width: `${3 + (i % 4)}px`,
              height: `${3 + (i % 4)}px`,
              background: i % 3 === 0 ? accentColor : i % 3 === 1 ? "#7c3aed" : "#0ea5e9",
              opacity: 0.35
            }}
            animate={{ y: [0, -(80 + i * 8), 0], opacity: [0, 0.5, 0] }}
            transition={{
              duration: 6 + (i % 5),
              repeat: Infinity,
              delay: i * 0.4,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* ── Main Content ── */}
      <div className="container ind-container">
        <motion.div
          className="row align-items-center ind-row"
          variants={staggerParent}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >

          {/* ── LEFT COLUMN ── */}
          <div className="col-12 col-lg-5 mb-5 mb-lg-0">
            <motion.div variants={fadeUp} className="ind-label-row">
              <span className="ind-label-line" style={{ background: accentColor }} />
              <span className="ind-label-text">INDUSTRIES WE SERVE</span>
            </motion.div>

            <motion.h2 variants={fadeUp} className="ind-main-title">
              Empowering{" "}
              <span className="ind-title-plain">sectors</span>
              {" "}
              <motion.span
                className="ind-title-accent"
                key={activeIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  background: `linear-gradient(135deg, ${accentColor} 0%, #7c3aed 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}
              >
                driving real growth.
              </motion.span>
            </motion.h2>

            <motion.p variants={fadeUp} className="ind-desc">
              We design and deliver bespoke software architectures, cloud infrastructures, and high-performance applications carefully engineered for each industry vertical.
            </motion.p>

            {/* Active industry tag */}
            <motion.div variants={fadeUp} className="ind-active-tag">
              <motion.span
                className="ind-tag-icon"
                key={activeIndex + "icon"}
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                style={{ color: accentColor, background: `${accentColor}18` }}
              >
                {current.icon}
              </motion.span>
              <motion.div
                key={activeIndex + "name"}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="ind-tag-name" style={{ color: accentColor }}>{current.name} Sector</div>
                <div className="ind-tag-sub">{current.tagline}</div>
              </motion.div>
            </motion.div>

            {/* Navigation */}
            <motion.div variants={fadeUp} className="ind-nav-row">
              <button
                className="ind-nav-btn"
                onClick={handlePrev}
                onMouseEnter={() => setHoveredNav("prev")}
                onMouseLeave={() => setHoveredNav(null)}
                style={hoveredNav === "prev" ? { background: accentColor, borderColor: accentColor, color: "#fff" } : {}}
                aria-label="Previous"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>

              <button
                className="ind-nav-btn"
                onClick={handleNext}
                onMouseEnter={() => setHoveredNav("next")}
                onMouseLeave={() => setHoveredNav(null)}
                style={hoveredNav === "next" ? { background: accentColor, borderColor: accentColor, color: "#fff" } : {}}
                aria-label="Next"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>

              <span className="ind-fraction">
                <span className="ind-fraction-cur" style={{ color: accentColor }}>
                  {String(activeIndex + 1).padStart(2, "0")}
                </span>
                <span className="ind-fraction-sep"> / </span>
                <span>{String(industries.length).padStart(2, "0")}</span>
              </span>
            </motion.div>

            {/* Dot pills */}
            <motion.div variants={fadeUp} className="ind-dots">
              {industries.map((ind, i) => (
                <button
                  key={i}
                  className={`ind-dot ${i === activeIndex ? "active" : ""}`}
                  onClick={() => { setDirection(i > activeIndex ? 1 : -1); setActiveIndex(i); }}
                  style={i === activeIndex ? { background: accentColor, boxShadow: `0 0 10px ${accentColor}66` } : {}}
                  aria-label={`Go to ${ind.name}`}
                />
              ))}
            </motion.div>
          </div>

          {/* ── RIGHT COLUMN — Slide Card ── */}
          <div className="col-12 col-lg-7 d-flex justify-content-center">
            <motion.div
              variants={fadeUp}
              className="ind-card"
              style={{ borderColor: `${accentColor}30` }}
            >
              {/* Card shimmer border glow */}
              <motion.div
                className="ind-card-glow"
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                style={{ background: `radial-gradient(ellipse at 50% 0%, ${accentColor}25 0%, transparent 60%)` }}
              />

              {/* Large decorative quote */}
              <div className="ind-quote-mark" style={{ color: `${accentColor}15` }}>"</div>

              {/* Slide Content */}
              <div className="ind-slide-body">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={activeIndex}
                    custom={direction}
                    variants={cardVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="ind-slide-inner"
                  >
                    {/* Quote text */}
                    <p className="ind-quote-text">
                      "{current.desc}"
                    </p>

                    {/* Divider */}
                    <motion.div
                      className="ind-divider"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      style={{ background: `linear-gradient(90deg, ${accentColor}, transparent)` }}
                    />

                    {/* Profile badge */}
                    <div className="ind-profile">
                      <motion.div
                        className="ind-profile-icon"
                        style={{ background: `${accentColor}15`, border: `1.5px solid ${accentColor}40`, color: accentColor }}
                        whileHover={{ scale: 1.1, rotate: 8 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {current.icon}
                      </motion.div>
                      <div className="ind-profile-info">
                        <h4 className="ind-profile-name" style={{ color: accentColor }}>{current.name} Sector</h4>
                        <span className="ind-profile-tagline">{current.tagline}</span>
                        <div className="ind-tags-row">
                          {current.techs.map((t, i) => (
                            <span
                              key={i}
                              className="ind-tech-tag"
                              style={{ background: `${accentColor}12`, color: accentColor, border: `1px solid ${accentColor}30` }}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Progress bar */}
              <div className="ind-progress-bar-wrap">
                <motion.div
                  className="ind-progress-bar"
                  key={activeIndex}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 5.5, ease: "linear" }}
                  style={{ background: accentColor }}
                />
              </div>
            </motion.div>
          </div>

        </motion.div>
      </div>

      {/* ── CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,400;1,600;1,700&family=Manrope:wght@300;400;500;600;700;800&display=swap');

        .ind-root {
          position: relative;
          overflow: hidden;
          background: linear-gradient(150deg, #fdf9f4 0%, #faf5ef 40%, #f5f0e8 70%, #fdf9f4 100%);
          min-height: 680px;
          display: flex;
          align-items: center;
          padding: 5rem 0;
        }

        /* Background Layer */
        .ind-bg-layer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }
        .ind-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
        }
        .ind-orb-1 {
          width: 500px;
          height: 500px;
          top: -150px;
          right: -100px;
          background: radial-gradient(circle, rgba(36, 62, 124, 0.09) 0%, transparent 70%);
        }
        .ind-orb-2 {
          width: 400px;
          height: 400px;
          bottom: -100px;
          left: -80px;
          background: radial-gradient(circle, rgba(36, 142, 203, 0.08) 0%, transparent 70%);
        }
        .ind-orb-3 {
          width: 300px;
          height: 300px;
          top: 40%;
          left: 40%;
          background: radial-gradient(circle, rgba(57, 174, 203, 0.07) 0%, transparent 70%);
        }
        .ind-dot-grid {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(36,62,124,0.06) 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .ind-particle {
          position: absolute;
          bottom: -10px;
          border-radius: 50%;
        }

        /* Container */
        .ind-container {
          position: relative;
          z-index: 2;
        }
        .ind-row {
          gap: 0;
        }

        /* Label */
        .ind-label-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .ind-label-line {
          display: block;
          width: 30px;
          height: 2px;
          border-radius: 2px;
          flex-shrink: 0;
        }
        .ind-label-text {
          font-family: 'Manrope', sans-serif;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(36,62,124,0.55);
        }

        /* Title */
        .ind-main-title {
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(2.2rem, 5vw, 3.4rem);
          font-weight: 700;
          color: #0f172a;
          line-height: 1.18;
          letter-spacing: -1.5px;
          margin-bottom: 1.5rem;
        }
        .ind-title-plain {
          color: #0f172a;
          font-weight: 700;
        }
        .ind-title-accent {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-weight: 700;
          display: inline-block;
          letter-spacing: -0.5px;
        }

        /* Description */
        .ind-desc {
          font-family: 'Manrope', sans-serif;
          font-size: 1rem;
          color: #64748b;
          line-height: 1.8;
          max-width: 420px;
          margin-bottom: 2rem;
          font-weight: 400;
        }

        /* Active tag */
        .ind-active-tag {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.25rem;
          background: rgba(36,62,124,0.05);
          border: 1px solid rgba(36,62,124,0.12);
          border-radius: 16px;
          margin-bottom: 2rem;
          backdrop-filter: blur(12px);
        }
        .ind-tag-icon {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .ind-tag-name {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1rem;
          font-weight: 700;
          margin-bottom: 0.15rem;
        }
        .ind-tag-sub {
          font-family: 'Manrope', sans-serif;
          font-size: 0.78rem;
          color: #64748b;
          font-weight: 500;
        }

        /* Nav */
        .ind-nav-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .ind-nav-btn {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          border: 1.5px solid rgba(36,62,124,0.18);
          background: rgba(255,255,255,0.75);
          color: #243e7c;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16,1,0.3,1);
          backdrop-filter: blur(8px);
          box-shadow: 0 2px 10px rgba(36,62,124,0.06);
        }
        .ind-nav-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.3);
        }
        .ind-fraction {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.1rem;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 1px;
        }
        .ind-fraction-cur {
          font-size: 1.5rem;
        }

        /* Dots */
        .ind-dots {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .ind-dot {
          height: 4px;
          width: 14px;
          border-radius: 10px;
          background: rgba(36,62,124,0.15);
          border: none;
          padding: 0;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.16,1,0.3,1);
        }
        .ind-dot.active {
          width: 28px;
        }
        .ind-dot:hover:not(.active) {
          background: rgba(36,62,124,0.3);
        }

        /* Card */
        .ind-card {
          width: 100%;
          max-width: 620px;
          background: rgba(255,255,255,0.78);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255,255,255,0.65);
          border-radius: 28px;
          padding: 3.5rem 3rem 2rem;
          position: relative;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(36,62,124,0.1), 0 4px 20px rgba(36,62,124,0.06), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .ind-card-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 200px;
          pointer-events: none;
          z-index: 0;
        }
        .ind-quote-mark {
          position: absolute;
          top: -20px;
          left: 30px;
          font-family: 'Playfair Display', serif;
          font-size: 10rem;
          line-height: 1;
          pointer-events: none;
          z-index: 0;
        }
        .ind-slide-body {
          position: relative;
          z-index: 1;
          min-height: 260px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .ind-slide-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* Quote */
        .ind-quote-text {
          font-family: 'Playfair Display', serif;
          font-size: 1.12rem;
          line-height: 1.8;
          color: #334155;
          font-weight: 400;
          font-style: italic;
          margin-bottom: 2rem;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 5;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        /* Divider */
        .ind-divider {
          height: 1px;
          width: 100%;
          margin-bottom: 1.5rem;
          transform-origin: left;
          opacity: 0.5;
        }

        /* Profile */
        .ind-profile {
          display: flex;
          align-items: flex-start;
          gap: 1.2rem;
        }
        .ind-profile-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          cursor: default;
        }
        .ind-profile-info {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }
        .ind-profile-name {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
          margin: 0;
        }
        .ind-profile-tagline {
          font-family: 'Manrope', sans-serif;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .ind-tags-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          margin-top: 0.5rem;
        }
        .ind-tech-tag {
          font-family: 'Manrope', sans-serif;
          font-size: 0.72rem;
          font-weight: 600;
          padding: 0.2rem 0.65rem;
          border-radius: 20px;
          letter-spacing: 0.3px;
        }

        /* Progress bar */
        .ind-progress-bar-wrap {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: rgba(36,62,124,0.07);
          border-radius: 0 0 28px 28px;
          overflow: hidden;
        }
        .ind-progress-bar {
          height: 100%;
          width: 100%;
          transform-origin: left;
          border-radius: 0 0 28px 28px;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .ind-root { padding: 3rem 0; min-height: auto; }
          .ind-card { padding: 2.5rem 1.75rem 1.75rem; }
          .ind-quote-text { font-size: 1rem; }
          .ind-main-title { font-size: 2.2rem; }
          .ind-desc { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default IndustriesSection;
